import { useState } from "react";

import ExerciseCard from "../components/ExerciseCard";
import VideoPlayer from "../components/VideoPlayer";
import VideoTaskCounter from "../components/VideoTaskCounter";
import ProgressRing from "../components/ProgressRing";
import { NumberInput } from "../components/NumberInput";
import { ParagraphEditor } from "../components/ParagraphEditor";
import type { BlockItem } from "../types";
import { useMainButton } from "../tg";

// Песочница UI. Изолированное место для проверки визуальных вариантов
// компонентов до раскатки на основные экраны. В БД ничего не пишет.
//
// Текущий мокап — редизайн ДЗ: чекбокс «Дневные» переезжает с упражнения
// на блок целиком. В ДЗ отображается весь блок (видео + текст +
// упражнения), а не отдельное упражнение.

// --- Раздел 1: мокап-данные блока «Тренировка губ» --------------------------

const VIDEO_URL =
  "https://demosfen.palettelab.ru/s3/audio/0c012beb-8783-4e98-964a-bc470b7919f3.mp4";

type DudokaItem = Extract<BlockItem, { type: "exercise" }>;

const EXERCISE_DUDOCHKA: DudokaItem = {
  type: "exercise",
  title: "Дудочка",
  description_md:
    "Сложите губы в трубочку и вытягивайте вперед, максимально, насколько это вообще возможно. Губы будет жечь — это значит мышцы дикции качаются.",
  repetitions_per_day: 3,
  timer_seconds: 30,
  metronome_bpm: null,
};

const EXERCISE_STRETCH: DudokaItem = {
  type: "exercise",
  title: "Растяжка губ",
  description_md:
    "Растягивайте губы в стороны, как будто широкая улыбка, удерживайте — и обратно в трубочку.",
  repetitions_per_day: 2,
  timer_seconds: 20,
  metronome_bpm: null,
};

// --- Mock-данные для секции «Концепты Hero и Feature карточек» --------------

const HERO_MOCK = {
  title: "Пульс речи 2.0",
  lessons_completed: 7,
  lessons_total: 20,
  progress_pct: 35,
  current_lesson: 8,
  streak_days: 12,
  minutes_today: 15,
};

const FEATURE_MOCK = {
  title: "Аудио-курс",
  tracks_completed: 4,
  tracks_total: 12,
  progress_pct: 33,
  last_track: "Урок 5 — Тембр голоса",
  track_length_min: 8,
};

type Props = {
  onBack: () => void;
};

export default function AdminUITest({ onBack: _onBack }: Props) {
  useMainButton(null);

  // --- Демо NumberInput ---
  const [demoOrder, setDemoOrder] = useState(1);
  const [demoBpm, setDemoBpm] = useState(60);

  // --- Демо Markdown Toolbar ---
  const [demoMd, setDemoMd] = useState(
    "**Жирный**, _курсив_, `код`.\n\n- пункт 1\n- пункт 2",
  );

  return (
    <div className="screen screen-with-sticky-header">
      <SandboxStyles />

      <header className="lesson-header-sticky">
        <h1>Песочница UI</h1>
        <p className="subtitle">Редизайн ДЗ — чекбокс «Дневные» на блоке</p>
      </header>

      {/* ============== Демо: NumberInput ============== */}
      <section className="sandbox-section">
        <h2 className="sandbox-section-title">NumberInput demo</h2>
        <p className="sandbox-section-hint">
          Кастомный контрол [−][число][+] взамен нативного input type=number с
          встроенными стрелочками.
        </p>
        <div className="block-item-separated">
          <label className="admin-field">
            <span>Display order (шаг 1)</span>
            <NumberInput
              value={demoOrder}
              onChange={setDemoOrder}
              min={1}
              step={1}
            />
          </label>
          <label className="admin-field">
            <span>Метроном, BPM (шаг 10, 20-300)</span>
            <NumberInput
              value={demoBpm}
              onChange={setDemoBpm}
              min={20}
              max={300}
              step={10}
            />
          </label>
        </div>
      </section>

      {/* ============== Демо: Markdown Toolbar ============== */}
      <section className="sandbox-section">
        <h2 className="sandbox-section-title">Markdown Toolbar demo</h2>
        <p className="sandbox-section-hint">
          Тулбар над textarea: B / I / &lt;/&gt; / 🔗 / • / 1. / H1 / H2.
          Работает с выделением и курсором.
        </p>
        <ParagraphEditor value={demoMd} onChange={setDemoMd} />
      </section>

      {/* ============== Демо: VideoPlayer — GIF-режим ============== */}
      <section className="sandbox-section">
        <h2 className="sandbox-section-title">
          VideoPlayer — GIF-режим (loop+autoplay+muted)
        </h2>
        <p className="sandbox-section-hint">
          Для конвертированных из GIF mp4 — без кастомного UI, автоплей и
          зацикливание. Сравнить с обычным режимом ниже. URL — временное
          mp4-видео из мокапа; после деплоя загрузите тестовый GIF в админку
          и подставьте сюда реальный URL сконвертированного файла.
        </p>
        <VideoPlayer url={VIDEO_URL} title="GIF-режим" loop={true} />
        <h3
          className="sandbox-section-title"
          style={{ marginTop: 24 }}
        >
          Обычный режим (для сравнения)
        </h3>
        <VideoPlayer url={VIDEO_URL} title="Обычный режим" loop={false} />
      </section>

      {/* ============== Раздел 1: как выглядит в приложении ============== */}
      <section className="sandbox-section">
        <h2 className="sandbox-section-title">
          Дневное ДЗ — блок целиком
        </h2>
        <p className="sandbox-section-hint">
          Как будет выглядеть карточка одного блока в DailyHomework если
          чекбокс «Дневные» переедет с упражнения на блок.
        </p>
        <p className="sandbox-section-hint sandbox-section-hint-accent">
          ← в этом блоке есть упражнения, поэтому появляется чекбокс «Дневные»
        </p>

        <article className="block-card">
          {/* Шапка блока: хлебные крошки + заголовок + общий чекбокс ДЗ */}
          <div className="block-item-separated block-item-separated-first">
            <div className="sandbox-block-header">
              <div className="exercise-lesson-meta">Раздел Голос › Урок 1</div>
              <h2 className="block-title">Тренировка губ</h2>
              <label className="sandbox-block-dz-checkbox">
                <input type="checkbox" defaultChecked readOnly />
                <span>Дневные</span>
              </label>
            </div>
          </div>

          {/* Items блока */}
          <div className="block-item-separated">
            <VideoPlayer
              url={VIDEO_URL}
              title={null}
              width={480}
              height={480}
              caption="Демонстрация упражнения — обрати внимание на положение челюсти."
            />
          </div>

          <div className="block-item-separated">
            <div className="block-text">
              <p>
                Сложите губы в трубочку и вытягивайте вперед, максимально,
                насколько это вообще возможно. Губы будет жечь — это значит
                мышцы дикции качаются.
              </p>
            </div>
          </div>

          {/* Edge-case: серая плашка с буквосочетаниями (fenced code из markdown
              рендерится как <pre><code>). Раньше длинная строка вываливалась за
              правый край карточки — проверяем что теперь переносится. */}
          <div className="block-item-separated">
            <div className="block-text">
              <p>Сочетания звуков:</p>
              <pre>
                <code>
                  РЖЛИ, РЖЛЭ, РЖЛА, РЖЛО, РЖЛУ, РЖЛЫ, РЖЛЕ
                </code>
              </pre>
              <p>Inline-вариант: <code>РЖЛИ,РЖЛЭ,РЖЛА,РЖЛО,РЖЛУ,РЖЛЫ,РЖЛЕ</code> — без пробелов.</p>
            </div>
          </div>

          <div className="block-item-separated block-item-card-type">
            <ExerciseCard
              exercise={EXERCISE_DUDOCHKA}
              mode="lesson"
              count={0}
              onComplete={() => console.log("[sandbox] dudochka complete")}
            />
          </div>

          <div className="block-item-separated block-item-card-type">
            <ExerciseCard
              exercise={EXERCISE_STRETCH}
              mode="lesson"
              count={0}
              onComplete={() => console.log("[sandbox] stretch complete")}
            />
          </div>
        </article>

        {/* Нижняя навигация — как в DailyHomework, неактивные для мокапа */}
        <div className="lesson-bottom-bar lesson-bottom-bar-inline">
          <button
            type="button"
            className="lesson-nav-btn lesson-nav-btn-secondary"
            disabled
          >
            ← Назад
          </button>
          <button
            type="button"
            className="lesson-nav-btn lesson-nav-btn-primary"
            disabled
          >
            Далее →
          </button>
        </div>

        <p className="sandbox-explanatory-note">
          Если в блоке нет упражнений — он не имеет смысла как дневное ДЗ, в
          этот список не попадает.
        </p>
      </section>

      {/* ============== Раздел 2: как выглядит в админке ============== */}
      <section className="sandbox-section">
        <h2 className="sandbox-section-title">
          Форма блока в админке — чекбокс «Дневные» переезжает на блок
        </h2>
        <p className="sandbox-section-hint">
          На уровне блока — только чекбокс «Дневные». Повторения и механика
          остаются у каждого упражнения. Чекбокс появляется автоматически,
          если внутри блока есть хотя бы одно упражнение.
        </p>

        <div className="admin-form">
          {/* --- Параметры блока (новое) --- */}
          <div className="sandbox-admin-group">
            <div className="sandbox-admin-group-title">Параметры блока</div>

            <label className="admin-field">
              <span>Заголовок блока</span>
              <input type="text" defaultValue="Тренировка губ" readOnly />
            </label>

            <label className="admin-field admin-field-checkbox">
              <input type="checkbox" defaultChecked readOnly />
              <span>Дневные</span>
            </label>

            <p className="admin-field-hint">
              Если включено — блок попадает в ежедневное ДЗ. У каждого
              упражнения внутри свой счётчик повторений (см. ниже).
            </p>
          </div>

          {/* --- Компоненты блока --- */}
          <div className="sandbox-admin-group">
            <div className="sandbox-admin-group-title">Компоненты блока</div>

            <div className="admin-items">
              {/* item #1 — video */}
              <div className="admin-item">
                <div className="admin-item-header">
                  <span className="admin-item-type">#1 · Видео</span>
                </div>
                <input
                  type="url"
                  defaultValue={VIDEO_URL}
                  readOnly
                />
                <input
                  type="text"
                  defaultValue=""
                  placeholder="Заголовок (опционально)"
                  style={{ marginTop: 6 }}
                  readOnly
                />
                <textarea
                  rows={2}
                  defaultValue="Демонстрация упражнения — обрати внимание на положение челюсти."
                  style={{ marginTop: 6 }}
                  readOnly
                />
              </div>

              {/* item #2 — paragraph */}
              <div className="admin-item">
                <div className="admin-item-header">
                  <span className="admin-item-type">#2 · Текст</span>
                </div>
                <textarea
                  rows={4}
                  defaultValue="Сложите губы в трубочку и вытягивайте вперед, максимально, насколько это вообще возможно. Губы будет жечь — это значит мышцы дикции качаются."
                  readOnly
                />
              </div>

              {/* item #3 — exercise «Дудочка» */}
              <div className="admin-item">
                <div className="admin-item-header">
                  <span className="admin-item-type">#3 · Упражнение</span>
                </div>
                <input type="text" defaultValue="Дудочка" readOnly />
                <textarea
                  rows={3}
                  defaultValue="Сложите губы в трубочку и удерживайте."
                  style={{ marginTop: 6 }}
                  readOnly
                />
                <label className="admin-field" style={{ marginTop: 6 }}>
                  <span>Режим</span>
                  <select defaultValue="timer" disabled>
                    <option value="timer">Таймер</option>
                    <option value="metronome">Метроном</option>
                    <option value="none">Без механики</option>
                  </select>
                </label>
                <label className="admin-field">
                  <span>Таймер, секунд</span>
                  <input type="number" defaultValue={30} readOnly />
                </label>
                <label className="admin-field">
                  <span>Повторений в день</span>
                  <input type="number" defaultValue={3} readOnly />
                </label>
              </div>

              {/* item #4 — exercise «Растяжка губ» */}
              <div className="admin-item">
                <div className="admin-item-header">
                  <span className="admin-item-type">#4 · Упражнение</span>
                </div>
                <input type="text" defaultValue="Растяжка губ" readOnly />
                <textarea
                  rows={3}
                  defaultValue="Растягивайте губы в стороны, как будто широкая улыбка."
                  style={{ marginTop: 6 }}
                  readOnly
                />
                <label className="admin-field" style={{ marginTop: 6 }}>
                  <span>Режим</span>
                  <select defaultValue="timer" disabled>
                    <option value="timer">Таймер</option>
                    <option value="metronome">Метроном</option>
                    <option value="none">Без механики</option>
                  </select>
                </label>
                <label className="admin-field">
                  <span>Таймер, секунд</span>
                  <input type="number" defaultValue={20} readOnly />
                </label>
                <label className="admin-field">
                  <span>Повторений в день</span>
                  <input type="number" defaultValue={2} readOnly />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* --- Разделитель между двумя вариантами админ-формы --- */}
        <hr className="sandbox-form-separator" />

        {/* --- Второй мокап админ-формы — блок БЕЗ упражнений --- */}
        <p className="sandbox-section-hint">
          Тот же экран, но для блока без упражнений (только контент): чекбокс
          «Дневные» не показывается.
        </p>

        <div className="admin-form">
          {/* Параметры блока — без чекбокса «Дневные» */}
          <div className="sandbox-admin-group">
            <div className="sandbox-admin-group-title">Параметры блока</div>

            <label className="admin-field">
              <span>Заголовок блока</span>
              <input type="text" defaultValue="Введение в курс" readOnly />
            </label>

            <p className="admin-field-hint sandbox-empty-checkbox-hint">
              Чекбокс «Дневные» появится автоматически, как только добавишь
              хотя бы одно упражнение в блок.
            </p>
          </div>

          {/* Компоненты блока — только контент, без exercise */}
          <div className="sandbox-admin-group">
            <div className="sandbox-admin-group-title">Компоненты блока</div>

            <div className="admin-items">
              {/* item #1 — paragraph */}
              <div className="admin-item">
                <div className="admin-item-header">
                  <span className="admin-item-type">#1 · Текст</span>
                </div>
                <textarea
                  rows={3}
                  defaultValue="Это первый урок, познакомимся с курсом."
                  readOnly
                />
              </div>

              {/* item #2 — video */}
              <div className="admin-item">
                <div className="admin-item-header">
                  <span className="admin-item-type">#2 · Видео</span>
                </div>
                <input type="url" defaultValue={VIDEO_URL} readOnly />
                <input
                  type="text"
                  defaultValue=""
                  placeholder="Заголовок (опционально)"
                  style={{ marginTop: 6 }}
                  readOnly
                />
                <textarea
                  rows={2}
                  defaultValue=""
                  placeholder="Подпись (опционально)"
                  style={{ marginTop: 6 }}
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== Раздел 3: концепты Hero и Feature с прогрессом ============== */}
      <section className="sandbox-section">
        <h2 className="sandbox-section-title">
          Концепты Hero и Feature карточек
        </h2>
        <p className="sandbox-section-hint">
          Илье понравился вариант 1 в обеих секциях — Hero «Кольцо в углу +
          детали» и Feature «Mini-ring + play». Ниже по 4 вариации первого
          варианта в каждой секции: схема та же, отличаются расположением,
          размерами и расстановкой кнопок.
        </p>

        {/* ----- Hero — «Пульс речи 2.0» ----- */}
        <h3 className="test-variant-group-title">Hero — «Пульс речи 2.0»</h3>

        {/* ---- A1 ---- */}
        <p className="test-variant-caption">Вариант A1 — Эталон, баг чинён</p>
        <div className="section-hero test-variant-hero-a1">
          <div className="test-variant-hero-ring-slot">
            <ProgressRing
              percent={HERO_MOCK.progress_pct}
              size={60}
              stroke={6}
            >
              <span className="test-variant-ring-ratio-sm">
                {HERO_MOCK.lessons_completed}/{HERO_MOCK.lessons_total}
              </span>
            </ProgressRing>
          </div>
          <div className="test-variant-hero-content">
            <div className="section-hero-title">{HERO_MOCK.title}</div>
            <div className="section-hero-meta">
              Урок {HERO_MOCK.current_lesson} · {HERO_MOCK.progress_pct}%
              пройдено
            </div>
          </div>
          <div className="test-variant-hero-actions test-variant-hero-actions-row">
            <button
              type="button"
              className="section-cta test-variant-cta-secondary"
              disabled
            >
              Все уроки
            </button>
            <button type="button" className="section-cta" disabled>
              Продолжить
            </button>
          </div>
        </div>

        {/* ---- A2 ---- */}
        <p className="test-variant-caption">
          Вариант A2 — Кольцо крупнее, кнопки компактные
        </p>
        <div className="section-hero test-variant-hero-a2">
          <div className="test-variant-hero-ring-slot">
            <ProgressRing
              percent={HERO_MOCK.progress_pct}
              size={76}
              stroke={7}
            >
              <span className="test-variant-ring-ratio-md">
                {HERO_MOCK.lessons_completed}/{HERO_MOCK.lessons_total}
              </span>
            </ProgressRing>
          </div>
          <div className="test-variant-hero-content">
            <div className="section-hero-title test-variant-hero-title-sm">
              {HERO_MOCK.title}
            </div>
            <div className="section-hero-meta">
              Урок {HERO_MOCK.current_lesson} · {HERO_MOCK.progress_pct}%
              пройдено
            </div>
          </div>
          <div className="test-variant-hero-actions test-variant-hero-actions-row-start">
            <button
              type="button"
              className="section-cta section-cta-compact"
              disabled
            >
              Продолжить
            </button>
            <button
              type="button"
              className="section-cta section-cta-compact test-variant-cta-secondary"
              disabled
            >
              Все уроки
            </button>
          </div>
        </div>

        {/* ---- A3 ---- */}
        <p className="test-variant-caption">Вариант A3 — Одна большая CTA</p>
        <div className="section-hero test-variant-hero-a3">
          <div className="test-variant-hero-ring-slot">
            <ProgressRing
              percent={HERO_MOCK.progress_pct}
              size={60}
              stroke={6}
            >
              <span className="test-variant-ring-ratio-sm">
                {HERO_MOCK.lessons_completed}/{HERO_MOCK.lessons_total}
              </span>
            </ProgressRing>
          </div>
          <div className="test-variant-hero-content">
            <div className="section-hero-title">{HERO_MOCK.title}</div>
            <div className="section-hero-meta">
              Урок {HERO_MOCK.current_lesson} · {HERO_MOCK.progress_pct}%
              пройдено
            </div>
          </div>
          <div className="test-variant-hero-actions">
            <button
              type="button"
              className="section-cta test-variant-cta-block"
              disabled
            >
              Продолжить →
            </button>
          </div>
        </div>

        {/* ---- A4 ---- */}
        <p className="test-variant-caption">Вариант A4 — Кольцо + KPI-плашка</p>
        <div className="section-hero test-variant-hero-a4">
          <div className="test-variant-hero-ring-slot test-variant-hero-ring-slot-stack">
            <ProgressRing
              percent={HERO_MOCK.progress_pct}
              size={60}
              stroke={6}
            >
              <span className="test-variant-ring-ratio-sm">
                {HERO_MOCK.lessons_completed}/{HERO_MOCK.lessons_total}
              </span>
            </ProgressRing>
            <div className="test-variant-streak-pill">
              🔥 {HERO_MOCK.streak_days} дней
            </div>
          </div>
          <div className="test-variant-hero-content">
            <div className="section-hero-title">{HERO_MOCK.title}</div>
            <div className="section-hero-meta">
              Урок {HERO_MOCK.current_lesson} · {HERO_MOCK.progress_pct}%
              пройдено
            </div>
          </div>
          <div className="test-variant-hero-actions test-variant-hero-actions-row">
            <button type="button" className="section-cta" disabled>
              Продолжить
            </button>
            <button
              type="button"
              className="section-cta test-variant-cta-secondary"
              disabled
            >
              Все уроки
            </button>
          </div>
        </div>

        {/* ----- Feature — «Аудио-курс» ----- */}
        <h3 className="test-variant-group-title">Feature — «Аудио-курс»</h3>

        {/* ---- B1 ---- */}
        <p className="test-variant-caption">Вариант B1 — Эталон</p>
        <div className="section-feature test-variant-feature-b1">
          <ProgressRing
            percent={FEATURE_MOCK.progress_pct}
            size={56}
            stroke={6}
          >
            <span className="test-variant-ring-ratio-xs">
              {FEATURE_MOCK.tracks_completed}/{FEATURE_MOCK.tracks_total}
            </span>
          </ProgressRing>
          <div className="section-feature-text">
            <div className="section-feature-title">{FEATURE_MOCK.title}</div>
            <div className="section-feature-meta">
              {FEATURE_MOCK.last_track}
            </div>
          </div>
          <button
            type="button"
            className="test-variant-play-btn"
            disabled
            aria-label="Продолжить"
          >
            ▶
          </button>
        </div>

        {/* ---- B2 ---- */}
        <p className="test-variant-caption">
          Вариант B2 — Кольцо крупнее, акцент на текущий урок
        </p>
        <div className="section-feature test-variant-feature-b2">
          <ProgressRing
            percent={FEATURE_MOCK.progress_pct}
            size={64}
            stroke={6}
          >
            <span className="test-variant-ring-ratio-sm">
              {FEATURE_MOCK.tracks_completed}/{FEATURE_MOCK.tracks_total}
            </span>
          </ProgressRing>
          <div className="section-feature-text">
            <div className="test-variant-feature-eyebrow">
              {FEATURE_MOCK.title}
            </div>
            <div className="test-variant-feature-headline">
              {FEATURE_MOCK.last_track}
            </div>
          </div>
          <button
            type="button"
            className="test-variant-play-btn test-variant-play-btn-md"
            disabled
            aria-label="Продолжить"
          >
            ▶
          </button>
        </div>

        {/* ---- B3 ---- */}
        <p className="test-variant-caption">Вариант B3 — Двойная meta</p>
        <div className="section-feature test-variant-feature-b3">
          <ProgressRing
            percent={FEATURE_MOCK.progress_pct}
            size={56}
            stroke={6}
          >
            <span className="test-variant-ring-ratio-xs">
              {FEATURE_MOCK.tracks_completed}/{FEATURE_MOCK.tracks_total}
            </span>
          </ProgressRing>
          <div className="section-feature-text">
            <div className="section-feature-title">{FEATURE_MOCK.title}</div>
            <div className="section-feature-meta">
              {FEATURE_MOCK.last_track}
            </div>
            <div className="section-feature-meta">
              {FEATURE_MOCK.track_length_min} мин · {FEATURE_MOCK.progress_pct}%
            </div>
          </div>
          <button
            type="button"
            className="test-variant-play-btn test-variant-play-btn-sm"
            disabled
            aria-label="Продолжить"
          >
            ▶
          </button>
        </div>

        {/* ---- B4 ---- */}
        <p className="test-variant-caption">
          Вариант B4 — Кнопка-таблетка вместо круга
        </p>
        <div className="section-feature test-variant-feature-b4">
          <ProgressRing
            percent={FEATURE_MOCK.progress_pct}
            size={56}
            stroke={6}
          >
            <span className="test-variant-ring-ratio-xs">
              {FEATURE_MOCK.tracks_completed}/{FEATURE_MOCK.tracks_total}
            </span>
          </ProgressRing>
          <div className="section-feature-text">
            <div className="section-feature-title">{FEATURE_MOCK.title}</div>
            <div className="section-feature-meta">
              {FEATURE_MOCK.last_track}
            </div>
          </div>
          <button
            type="button"
            className="section-cta section-cta-compact test-variant-cta-play-pill"
            disabled
          >
            ▶ Продолжить
          </button>
        </div>
      </section>

      {/* ============== Демо: VideoTaskCounter ============== */}
      <section className="sandbox-section">
        <h2 className="sandbox-section-title">VideoTaskCounter</h2>
        <p className="sandbox-section-hint">
          Счётчик повторов (заменил рекордер видео). Inc/dec идут реальным
          запросом на /lesson/video_record/{`{op}`} — в песочнице blockId
          фейковый, поэтому ответ будет 404, это ок.
        </p>

        <VideoTaskCounter
          blockId="00000000-0000-0000-0000-000000000000"
          itemIndex={0}
          title="Записать вашу версию"
          caption="Сделайте видеозапись своего выполнения упражнения и поставьте +1."
          maxCount={3}
          initialCount={0}
        />
      </section>
    </div>
  );
}

// Локальные стили только для песочницы — чтобы не плодить классы в общем styles.css
// до выбора окончательного варианта. Когда вариант утвердят — переедут в styles.css.
function SandboxStyles() {
  return (
    <style>{`
      .sandbox-section {
        margin: 24px 0 32px;
      }
      .sandbox-section-title {
        margin: 8px 0 4px;
        font-size: 18px;
        font-weight: 600;
        color: var(--tg-theme-text-color, #000);
      }
      .sandbox-section-hint {
        margin: 0 0 16px;
        font-size: 13px;
        color: var(--tg-theme-hint-color, #888);
      }
      .sandbox-block-header {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .sandbox-block-header .block-title {
        margin: 0;
      }
      .sandbox-block-dz-checkbox {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin-top: 4px;
        font-size: 14px;
        color: var(--tg-theme-text-color, #000);
        cursor: pointer;
      }
      .sandbox-block-dz-checkbox input[type="checkbox"] {
        width: 18px;
        height: 18px;
        accent-color: var(--tg-theme-button-color, #3390ec);
      }
      .lesson-bottom-bar-inline {
        position: static;
        margin-top: 12px;
      }
      .sandbox-admin-group {
        margin-bottom: 24px;
        padding: 12px;
        border: 1px dashed var(--tg-theme-hint-color, #ccc);
        border-radius: 12px;
      }
      .sandbox-admin-group-title {
        font-size: 14px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--tg-theme-hint-color, #888);
        margin-bottom: 12px;
      }
      .sandbox-section-hint-accent {
        margin-top: -8px;
        font-style: italic;
        color: var(--tg-theme-link-color, #3390ec);
      }
      .sandbox-explanatory-note {
        margin: 16px 0 0;
        padding: 10px 12px;
        font-size: 13px;
        color: var(--tg-theme-hint-color, #888);
        background: var(--tg-theme-secondary-bg-color, #f5f5f5);
        border-radius: 8px;
        border-left: 3px solid var(--tg-theme-hint-color, #ccc);
      }
      .sandbox-form-separator {
        margin: 32px 0;
        border: none;
        border-top: 1px solid var(--tg-theme-hint-color, #ccc);
        opacity: 0.4;
      }
      .sandbox-empty-checkbox-hint {
        margin-top: 8px;
        font-size: 12px;
        font-style: italic;
        color: var(--tg-theme-hint-color, #888);
      }

      /* --- Концепты Hero и Feature (test-variant-*) --- */

      .test-variant-group-title {
        margin: 24px 0 8px;
        font-size: 15px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--tg-theme-hint-color, #888);
      }
      .test-variant-caption {
        margin: 16px 0 6px;
        font-size: 12px;
        font-style: italic;
        color: var(--tg-theme-hint-color, #888);
      }

      .test-variant-cta-secondary {
        /* Спокойная "контурная" CTA — прозрачный фон + border, чтобы визуально
           отличаться от primary с акцентным фоном. Совпадает с проп-стилем
           .section-hero-cta-secondary в styles.css. */
        background: transparent;
        color: var(--tg-theme-text-color, #000);
        border: 1px solid var(--card-border);
      }
      .test-variant-cta-block {
        align-self: stretch;
        text-align: center;
        width: 100%;
      }
      .test-variant-ring-ratio-sm {
        font-size: 13px;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }
      .test-variant-ring-ratio-md {
        font-size: 15px;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }
      .test-variant-ring-ratio-xs {
        font-size: 12px;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }

      /* --- Hero: общая сетка ---
         Схема: title + meta слева, ring в правом верхнем углу,
         кнопки внизу во всю ширину карточки. Использован CSS grid с двумя
         колонками — текстовая 1fr (min-width:0 чтобы не выпирала за carda)
         и auto-колонка под ring. Кольцу даём свой "слот" с padding=16px
         чтобы фикс был стабильным — Илья жаловался, что ring 60px вылезает
         за правый край карточки. Причина была в том, что в старом варианте
         ring клался прямо в .section-hero (flex-column в основном CSS),
         а потом перекрывался grid'ом, который шёл "ребёнком" того же
         родителя — браузер на узких ширинах не оставлял место под ring.
         Теперь:
           - .section-hero мы переводим в grid целиком (нужны явные классы);
           - ring сидит в собственной grid-cell, не absolute;
           - padding карточки 18px формирует визуальный отступ;
           - .test-variant-hero-ring-slot стоит в правом верхнем углу,
             grid-column: 2 / 3, align-self: start.
         За пределы карточки выйти физически негде. */
      .section-hero[class*="test-variant-hero-"] {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        grid-template-rows: auto auto auto;
        column-gap: 14px;
        row-gap: 10px;
        align-items: start;
      }
      .test-variant-hero-ring-slot {
        grid-column: 2 / 3;
        grid-row: 1 / span 2;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
      }
      .test-variant-hero-ring-slot-stack {
        gap: 6px;
      }
      .test-variant-hero-content {
        grid-column: 1 / 2;
        grid-row: 1 / span 2;
        display: flex;
        flex-direction: column;
        gap: 6px;
        min-width: 0;
      }
      .test-variant-hero-actions {
        grid-column: 1 / -1;
        grid-row: 3 / 4;
        display: flex;
        flex-direction: row;
        gap: 10px;
        margin-top: 4px;
      }
      .test-variant-hero-actions > .section-cta {
        margin-top: 0;
        align-self: stretch;
        flex: 1 1 0;
        min-width: 0;
      }
      .test-variant-hero-actions-row > .section-cta {
        flex: 1 1 0;
      }
      .test-variant-hero-actions-row-start {
        justify-content: flex-start;
      }
      .test-variant-hero-actions-row-start > .section-cta {
        flex: 0 0 auto;
      }

      .test-variant-hero-title-sm {
        font-size: 20px;
      }

      .test-variant-streak-pill {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 3px 10px;
        border-radius: 999px;
        background: rgba(217, 137, 38, 0.16);
        color: #d98926;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.2;
        white-space: nowrap;
      }

      /* min-height варианты — чтобы карточки в песочнице выглядели похоже */
      .test-variant-hero-a1,
      .test-variant-hero-a2,
      .test-variant-hero-a3,
      .test-variant-hero-a4 {
        min-height: 150px;
      }

      /* --- Feature: схема ring + текст + кнопка справа --- */
      .test-variant-feature-b1,
      .test-variant-feature-b2,
      .test-variant-feature-b3,
      .test-variant-feature-b4 {
        gap: 14px;
        align-items: center;
      }
      .test-variant-feature-b3 {
        align-items: flex-start;
      }

      .test-variant-feature-eyebrow {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        opacity: 0.55;
      }
      .test-variant-feature-headline {
        font-size: 16px;
        font-weight: 700;
        line-height: 1.25;
      }

      .test-variant-play-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        background: var(--tg-theme-button-color, #2481cc);
        color: var(--tg-theme-button-text-color, #fff);
        border: none;
        font-size: 18px;
        line-height: 1;
        padding: 0 0 0 3px; /* визуально центрируем «треугольник» */
        cursor: pointer;
        flex-shrink: 0;
      }
      .test-variant-play-btn-md {
        width: 44px;
        height: 44px;
        font-size: 16px;
      }
      .test-variant-play-btn-sm {
        width: 48px;
        height: 48px;
        font-size: 16px;
      }

      .test-variant-cta-play-pill {
        white-space: nowrap;
        flex-shrink: 0;
      }
    `}</style>
  );
}
